<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Gestión de Componentes por Sección
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Selecciona una sección y gestiona sus componentes asociados
      </p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Panel izquierdo: Lista de Secciones -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Seleccionar Sección
          </h2>
          <!-- Filtro de secciones -->
          <div class="relative">
            <input 
              v-model="seccionSearch" 
              type="text" 
              placeholder="Buscar sección..." 
              class="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            />
            <svg class="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
        </div>
        
        <div class="p-4 max-h-96 overflow-y-auto">
          <div v-if="loadingSecciones" class="text-center py-8">
            <svg class="animate-spin h-8 w-8 text-violet-600 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <p class="text-gray-500 mt-2">Cargando secciones...</p>
          </div>
          
          <div v-else-if="filteredSecciones.length === 0" class="text-center py-8 text-gray-500">
            No se encontraron secciones
          </div>
          
          <div v-else class="space-y-2">
            <button
              v-for="seccion in filteredSecciones"
              :key="seccion.id"
              @click="selectSeccion(seccion)"
              :class="[
                'w-full text-left p-4 rounded-lg border transition-all duration-200',
                selectedSeccion?.id === seccion.id
                  ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300'
                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
              ]"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="font-medium">{{ seccion.nombre }}</h3>
                  <p class="text-sm opacity-75">ID: {{ seccion.id }}</p>
                </div>
                <svg v-if="selectedSeccion?.id === seccion.id" class="w-5 h-5 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Panel derecho: Gestión de Componentes -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
              Componentes de la Sección
            </h2>
            <button
              v-if="selectedSeccion"
              @click="showAddModal = true"
              class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar Componente
            </button>
          </div>
          
          <div v-if="selectedSeccion" class="bg-violet-50 dark:bg-violet-900/20 p-3 rounded-lg">
            <p class="text-sm text-violet-700 dark:text-violet-300">
              <strong>Sección seleccionada:</strong> {{ selectedSeccion.nombre }}
            </p>
          </div>
        </div>

        <div class="p-6">
          <div v-if="!selectedSeccion" class="text-center py-12">
            <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <p class="text-gray-500 text-lg">Selecciona una sección</p>
            <p class="text-gray-400 text-sm mt-1">para gestionar sus componentes</p>
          </div>

          <div v-else>
            <div v-if="loadingComponentes" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 text-violet-600 mx-auto" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              <p class="text-gray-500 mt-2">Cargando componentes...</p>
            </div>

            <div v-else-if="seccionComponentes.length === 0" class="text-center py-8">
              <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
              <p class="text-gray-500">No hay componentes asignados</p>
              <p class="text-gray-400 text-sm mt-1">Haz clic en "Agregar Componente" para comenzar</p>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="componente in seccionComponentes"
                :key="componente.id"
                class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900 dark:text-white">{{ componente.tipo_componente?.nombre || 'Sin nombre' }}</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <strong>Alias:</strong> {{ componente.alias || 'Sin alias' }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    @click="editComponente(componente)"
                    class="text-blue-500 hover:text-blue-700 p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Editar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3z"/>
                    </svg>
                  </button>
                  <button
                    @click="deleteComponente(componente)"
                    class="text-red-500 hover:text-red-700 p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Eliminar"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para agregar/editar componente -->
    <div v-if="showAddModal || showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ showEditModal ? 'Editar Componente' : 'Agregar Componente' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <form @submit.prevent="showEditModal ? updateComponente() : addComponente()" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Componente *
            </label>
            <select
              v-model="componenteForm.tipo_componente_id"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Selecciona un tipo de componente</option>
              <option v-for="tipo in tiposComponente" :key="tipo.id" :value="tipo.id">
                {{ tipo.nombre }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alias
            </label>
            <input
              v-model="componenteForm.alias"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ingrese un alias (opcional)"
            />
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? (showEditModal ? 'Actualizando...' : 'Agregando...') : (showEditModal ? 'Actualizar' : 'Agregar') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp'
import Swal from 'sweetalert2'

// Referencias reactivas
const secciones = ref([])
const tiposComponente = ref([])
const seccionComponentes = ref([])
const selectedSeccion = ref(null)
const seccionSearch = ref('')
const showAddModal = ref(false)
const showEditModal = ref(false)
const componenteForm = ref({
  id: null,
  seccion_id: null,
  tipo_componente_id: '',
  alias: ''
})

// Estados de carga
const loadingSecciones = ref(false)
const loadingComponentes = ref(false)

// Composable HTTP
const { get, post, del, loading, error, data } = useHttp()

// Computed para filtrar secciones
const filteredSecciones = computed(() => {
  if (!seccionSearch.value) return secciones.value
  return secciones.value.filter(seccion =>
    seccion.nombre.toLowerCase().includes(seccionSearch.value.toLowerCase())
  )
})

// Funciones para cargar datos
const fetchSecciones = async () => {
  loadingSecciones.value = true
  try {
    await get('secciones')
    secciones.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar secciones:', err)
    secciones.value = []
  } finally {
    loadingSecciones.value = false
  }
}

const fetchTiposComponente = async () => {
  try {
    await get('tipos-componentes')
    tiposComponente.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar tipos de componente:', err)
    tiposComponente.value = []
  }
}

const fetchSeccionComponentes = async () => {
  if (!selectedSeccion.value) return
  
  loadingComponentes.value = true
  try {
    await get(`seccion-tipos-componente?seccion_id=${selectedSeccion.value.id}`)
    seccionComponentes.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar componentes de la sección:', err)
    seccionComponentes.value = []
  } finally {
    loadingComponentes.value = false
  }
}

// Función para seleccionar una sección
const selectSeccion = (seccion) => {
  selectedSeccion.value = seccion
  fetchSeccionComponentes()
}

// Funciones del modal
const closeModal = () => {
  showAddModal.value = false
  showEditModal.value = false
  componenteForm.value = {
    id: null,
    seccion_id: null,
    tipo_componente_id: '',
    alias: ''
  }
}

const addComponente = async () => {
  try {
    const data = {
      seccion_id: selectedSeccion.value.id,
      tipo_componente_id: componenteForm.value.tipo_componente_id,
      alias: componenteForm.value.alias
    }
    
    await post('seccion-tipos-componente', data)
    closeModal()
    await fetchSeccionComponentes()
    
    Swal.fire({
      icon: 'success',
      title: 'Componente agregado',
      text: 'El componente se agregó exitosamente a la sección.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error al agregar el componente'
    })
  }
}

const editComponente = (componente) => {
  componenteForm.value = {
    id: componente.id,
    seccion_id: componente.seccion_id,
    tipo_componente_id: componente.tipo_componente_id,
    alias: componente.alias
  }
  showEditModal.value = true
}

const updateComponente = async () => {
  try {
    const data = {
      seccion_id: componenteForm.value.seccion_id,
      tipo_componente_id: componenteForm.value.tipo_componente_id,
      alias: componenteForm.value.alias
    }
    
    await post(`seccion-tipos-componente/${componenteForm.value.id}?_method=PUT`, data)
    closeModal()
    await fetchSeccionComponentes()
    
    Swal.fire({
      icon: 'success',
      title: 'Componente actualizado',
      text: 'El componente se actualizó exitosamente.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error al actualizar el componente'
    })
  }
}

const deleteComponente = async (componente) => {
  const result = await Swal.fire({
    title: '¿Eliminar componente?',
    text: `¿Seguro que deseas eliminar este componente de la sección? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (result.isConfirmed) {
    try {
      await del(`seccion-tipos-componente/${componente.id}`)
      await fetchSeccionComponentes()
      
      Swal.fire({
        title: '¡Eliminado!',
        text: 'El componente ha sido eliminado de la sección.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false
      })
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar el componente.', 'error')
    }
  }
}

// Cargar datos al montar el componente
onMounted(() => {
  fetchSecciones()
  fetchTiposComponente()
})
</script>